<template>
  <registry-page
      title="Справочник контрагентов"
      description="Единый список покупателей, поставщиков и других участников документов."
    >

    <app-data-table
      data-testid="counterparty-directory-table"
      title="Контрагенты"
      storage-key="counterparty-directory-table"
      :count="pagination.rowsNumber"
      :rows="sortedCounterparties"
      :columns="columns"
      :pagination="pagination"
      :state="tableState"
      :state-message="errorMessage"
      @update:pagination="pagination = $event"
      @request="onTableRequest"
      @reset="resetFilters"
      @retry="loadCounterparties()"
    >
      <template #filters>
        <filter-bar
          :model-value="search"
          search-label="Поиск по названию, ИНН или КПП"
          search-test-id="counterparty-directory-search"
          :has-filters="hasFilters"
          :applied-filters="appliedFilters"
          @search="applySearch"
          @reset="resetFilters"
          @remove="removeFilter"
        >
          <template #quick>
            <q-toggle
              dense
              :model-value="includeArchived"
              label="Показывать архив"
              data-testid="counterparty-directory-archived-toggle"
              @update:model-value="applyArchived"
            />
          </template>
          <template #advanced>
            <q-toggle
              :model-value="includeArchived"
              label="Показывать архив"
              @update:model-value="applyArchived"
            />
            <date-range-filter
              v-if="tableFixture.dateRange"
              :model-value="fixtureDateRange"
              class="q-mt-md"
              @apply-range="applyFixtureDateRange"
            />
          </template>
        </filter-bar>
      </template>

      <template v-if="canEditFinance" #actions>
        <q-btn
          unelevated
          color="primary"
          icon="add"
          no-caps
          label="Создать"
          data-testid="counterparty-directory-add"
          @click="openCreateDialog"
        />
      </template>

      <template v-slot:body-cell-name="props">
        <q-td :props="props">
          <div class="text-weight-medium">{{ displayName(props.row) }}</div>
          <div class="text-caption text-grey-7">{{ props.row.full_name || props.row.short_name || '' }}</div>
        </q-td>
      </template>

      <template v-slot:body-cell-inn_kpp="props">
        <q-td :props="props">
          {{ props.row.inn || '-' }}<span v-if="props.row.kpp"> / {{ props.row.kpp }}</span>
        </q-td>
      </template>

      <template v-slot:body-cell-status="props">
        <q-td :props="props">
          <status-badge
            :tone="props.row.is_active ? 'success' : 'neutral'"
            :icon="props.row.is_active ? 'check_circle' : 'archive'"
            :label="props.row.is_active ? 'Активен' : 'Архив'"
          />
        </q-td>
      </template>

      <template v-slot:body-cell-actions="props">
        <q-td :props="props" class="text-right">
          <q-btn
            v-if="canEditFinance"
            dense
            flat
            round
            icon="edit"
            color="primary"
            class="counterparty-row-action"
            aria-label="Редактировать"
            data-testid="counterparty-directory-edit"
            :disable="saving"
            @click.stop="openEditDialog(props.row)"
          />
          <q-btn
            v-if="canEditFinance && props.row.is_active !== false"
            dense
            flat
            round
            icon="archive"
            color="negative"
            class="counterparty-row-action"
            aria-label="Архивировать"
            data-testid="counterparty-directory-archive"
            :disable="saving"
            @click.stop="confirmArchive(props.row)"
          />
        </q-td>
      </template>
    </app-data-table>

    <q-dialog v-model="formDialog" :persistent="saving">
      <q-card class="counterparty-dialog">
        <q-card-section class="row items-center">
          <div class="text-h6">{{ formMode === 'create' ? 'Новый контрагент' : 'Редактировать контрагента' }}</div>
          <q-space />
          <q-btn dense flat round icon="close" v-close-popup aria-label="Закрыть" :disable="saving" />
        </q-card-section>

        <q-separator />

        <q-card-section>
          <q-banner v-if="formError" rounded class="bg-red-1 text-negative q-mb-md" data-testid="counterparty-directory-form-error">
            {{ formError }}
          </q-banner>
          <q-banner v-if="lookupMessage" rounded :class="lookupBannerClass" class="q-mb-md" data-testid="counterparty-directory-lookup-message">
            {{ lookupMessage }}
          </q-banner>
          <div v-if="lookupSource" class="counterparty-lookup-source q-mb-sm" data-testid="counterparty-directory-lookup-source">
            Источник: <strong>{{ lookupSource }}</strong>
          </div>
          <q-expansion-item
            v-if="lookupDiagnostics.length"
            dense
            icon="monitor_heart"
            label="Диагностика заполнения"
            class="counterparty-diagnostics q-mb-md"
            data-testid="counterparty-directory-lookup-diagnostics"
          >
            <q-list dense separator>
              <q-item v-for="(diagnostic, index) in lookupDiagnostics" :key="`${diagnostic.code || 'diagnostic'}-${index}`">
                <q-item-section>
                  <q-item-label>{{ diagnostic.message || diagnostic.code || diagnostic }}</q-item-label>
                  <q-item-label v-if="diagnostic.code" caption>{{ diagnostic.code }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-expansion-item>

          <div class="row q-col-gutter-md">
            <div class="col-12 col-md-4">
              <q-input dense outlined v-model="form.inn" label="ИНН" data-testid="counterparty-directory-inn" />
            </div>
            <div class="col-12 col-md-3">
              <q-input dense outlined v-model="form.kpp" label="КПП" data-testid="counterparty-directory-kpp" />
            </div>
            <div class="col-12 col-md-auto">
              <q-btn
                outline
                color="primary"
                icon="search"
                label="Заполнить"
                class="full-width"
                data-testid="counterparty-directory-lookup"
                :loading="lookupLoading"
                @click="lookupByInn"
              />
            </div>
          </div>

          <div class="row q-col-gutter-md q-mt-sm">
            <div class="col-12 col-md-6">
              <q-input dense outlined v-model="form.name" label="Наименование" data-testid="counterparty-directory-name" />
            </div>
            <div class="col-12 col-md-6">
              <q-input dense outlined v-model="form.full_name" label="Полное наименование" data-testid="counterparty-directory-full-name" />
            </div>
            <div class="col-12">
              <q-input dense outlined v-model="form.address" label="Адрес" data-testid="counterparty-directory-address" />
            </div>
            <div class="col-12 col-md-6">
              <q-input dense outlined v-model="form.legal_address" label="Юридический адрес" />
            </div>
            <div class="col-12 col-md-6">
              <q-input dense outlined v-model="form.actual_address" label="Фактический адрес" />
            </div>
            <div class="col-12 col-md-4">
              <q-input dense outlined v-model="form.ogrn" label="ОГРН" />
            </div>
            <div class="col-12 col-md-4">
              <q-input dense outlined v-model="form.phone" label="Телефон" />
            </div>
            <div class="col-12 col-md-4">
              <q-input dense outlined v-model="form.email" label="Email" />
            </div>
            <div class="col-12 col-md-4">
              <q-select
                dense
                outlined
                emit-value
                map-options
                v-model="form.type"
                :options="typeOptions"
                label="Тип"
              />
            </div>
            <div class="col-12 col-md-4">
              <q-select
                dense
                outlined
                emit-value
                map-options
                v-model="form.status"
                :options="statusOptions"
                label="Статус"
              />
            </div>
            <div class="col-12 col-md-4 flex items-center">
              <q-toggle v-model="form.enrich" label="Обогащать через DaData при сохранении" />
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat color="primary" label="Отмена" :disable="saving" v-close-popup />
          <q-btn
            unelevated
            color="primary"
            icon="save"
            label="Сохранить"
            data-testid="counterparty-directory-save"
            :loading="saving"
            @click="saveCounterparty"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </registry-page>
</template>

<script>
import {
  AppDataTable,
  DateRangeFilter,
  FilterBar,
  RegistryPage,
  StatusBadge
} from '../../../components/ui'

const emptyForm = () => ({
  id: null,
  inn: '',
  kpp: '',
  name: '',
  full_name: '',
  short_name: '',
  ogrn: '',
  type: 'unknown',
  status: 'unknown',
  address: '',
  legal_address: '',
  actual_address: '',
  phone: '',
  email: '',
  enrich: true
})

const normalizeDigits = (value) => String(value || '').replace(/\D/g, '')

function tableFixture() {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return {}
  return window.__SID_E2E_TABLE_PRIMITIVES__ || {}
}

export default {
  name: 'glossary',
  components: {
    AppDataTable,
    DateRangeFilter,
    FilterBar,
    RegistryPage,
    StatusBadge
  },
  data() {
    const fixture = tableFixture()
    const query = this.$route.query || {}
    return {
      requestId: null,
      mutationRequestId: null,
      lookupRequestId: null,
      search: String(query.q || ''),
      includeArchived: query.archived === '1',
      tableFixture: fixture,
      fixtureDateRange: { from: '', to: '' },
      loading: false,
      saving: false,
      lookupLoading: false,
      accessDenied: false,
      errorMessage: '',
      formError: '',
      lookupMessage: '',
      lookupState: 'idle',
      lookupSource: '',
      lookupDiagnostics: [],
      formDialog: false,
      formMode: 'create',
      form: emptyForm(),
      counterparties: [],
      handler: null,
      lookupHandler: null,
      createHandler: null,
      updateHandler: null,
      archiveHandler: null,
      readyHandler: null,
      accessDeniedHandler: null,
      pagination: {
        page: Math.max(1, Number(query.page || 1)),
        rowsPerPage: Math.max(1, Number(query.size || 20)),
        rowsNumber: 0,
        sortBy: String(query.sort || 'name'),
        descending: query.desc === '1'
      },
      typeOptions: [
        { label: 'Не указан', value: 'unknown' },
        { label: 'Юридическое лицо', value: 'LEGAL' },
        { label: 'ИП', value: 'INDIVIDUAL' },
        { label: 'Физическое лицо', value: 'PERSON' }
      ],
      statusOptions: [
        { label: 'Не указан', value: 'unknown' },
        { label: 'Действует', value: 'ACTIVE' },
        { label: 'Не действует', value: 'INACTIVE' },
        { label: 'Ликвидирован', value: 'LIQUIDATED' }
      ],
      columns: [
        { name: 'name', align: 'left', label: 'Название', field: (row) => this.displayName(row), sortable: true, required: true },
        { name: 'inn_kpp', align: 'left', label: 'ИНН / КПП', field: 'inn' },
        { name: 'address', align: 'left', label: 'Адрес', field: (row) => row.address || row.legal_address || '-', classes: 'column-priority-secondary', headerClasses: 'column-priority-secondary' },
        { name: 'status', align: 'left', label: 'Статус', field: 'is_active' },
        { name: 'actions', align: 'right', label: 'Действия', field: 'id', required: true }
      ]
    }
  },
  computed: {
    canEditFinance() {
      return this.$store.state.permissions.edit_fin_documents === true
    },
    tableState() {
      if (this.tableFixture.state && this.tableFixture.state !== 'ready') return this.tableFixture.state
      if (this.accessDenied) return 'access-denied'
      if (this.errorMessage) return 'error'
      if (this.loading && this.counterparties.length === 0) return 'loading'
      if (this.counterparties.length === 0) return this.hasFilters ? 'filtered-empty' : 'empty'
      return 'ready'
    },
    hasFilters() {
      return Boolean(this.search || this.includeArchived)
    },
    appliedFilters() {
      const filters = []
      if (this.search) filters.push({ key: 'search', label: `Поиск: ${this.search}` })
      if (this.includeArchived) filters.push({ key: 'archived', label: 'Включая архив' })
      return filters
    },
    sortedCounterparties() {
      const rows = [...this.counterparties]
      const sortBy = this.pagination.sortBy
      if (!sortBy) return rows
      const direction = this.pagination.descending ? -1 : 1
      const value = (row) => {
        if (sortBy === 'name') return this.displayName(row)
        if (sortBy === 'inn_kpp') return row.inn || ''
        if (sortBy === 'status') return row.is_active === false ? 0 : 1
        return row[sortBy] || ''
      }
      return rows.sort((left, right) => String(value(left)).localeCompare(String(value(right)), 'ru') * direction)
    },
    lookupBannerClass() {
      if (this.lookupState === 'success') {
        return 'bg-green-1 text-green-10'
      }
      if (this.lookupState === 'warning') {
        return 'bg-amber-2 text-brown-10'
      }
      return 'bg-red-1 text-negative'
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(query) {
        const previousDataKey = `${this.search}|${this.includeArchived}|${this.pagination.page}|${this.pagination.rowsPerPage}`
        this.applyQueryState(query)
        const nextDataKey = `${this.search}|${this.includeArchived}|${this.pagination.page}|${this.pagination.rowsPerPage}`
        if (previousDataKey !== nextDataKey) this.loadCounterparties()
      }
    }
  },
  mounted() {
    this.handler = this.onCounterparties
    this.lookupHandler = this.onLookupResult
    this.createHandler = this.onMutationResult
    this.updateHandler = this.onMutationResult
    this.archiveHandler = this.onArchiveResult
    this.readyHandler = () => this.loadCounterparties()
    this.accessDeniedHandler = this.onAccessDenied
    this.emitter.on('onGetCounterparties', this.handler)
    this.emitter.on('onLookupCounterpartyByInn', this.lookupHandler)
    this.emitter.on('onCreateCounterparty', this.createHandler)
    this.emitter.on('onUpdateCounterparty', this.updateHandler)
    this.emitter.on('onArchiveCounterparty', this.archiveHandler)
    this.emitter.on('accessDeniedErrorReceived', this.accessDeniedHandler)
    if (this.connection.isAuthenticated) {
      this.readyHandler()
    } else {
      this.emitter.on('connectionReady', this.readyHandler)
    }
  },
  beforeUnmount() {
    this.emitter.off('onGetCounterparties', this.handler)
    this.emitter.off('onLookupCounterpartyByInn', this.lookupHandler)
    this.emitter.off('onCreateCounterparty', this.createHandler)
    this.emitter.off('onUpdateCounterparty', this.updateHandler)
    this.emitter.off('onArchiveCounterparty', this.archiveHandler)
    this.emitter.off('accessDeniedErrorReceived', this.accessDeniedHandler)
    if (this.readyHandler) {
      this.emitter.off('connectionReady', this.readyHandler)
    }
  },
  methods: {
    onAccessDenied(response) {
      if (response.request_id === this.mutationRequestId) {
        this.saving = false
        if (this.formDialog) this.formError = 'Недостаточно прав для изменения контрагента'
        else this.errorMessage = 'Недостаточно прав для архивации контрагента'
        return
      }
      if (response.request_id !== this.requestId) return
      this.loading = false
      this.counterparties = []
      this.accessDenied = true
      this.errorMessage = ''
    },
    applyQueryState(query = {}) {
      this.search = String(query.q || '')
      this.includeArchived = query.archived === '1'
      this.pagination = {
        ...this.pagination,
        page: Math.max(1, Number(query.page || 1)),
        rowsPerPage: Math.max(1, Number(query.size || 20)),
        sortBy: String(query.sort || 'name'),
        descending: query.desc === '1'
      }
      this.fixtureDateRange = {
        from: String(query.from || ''),
        to: String(query.to || '')
      }
    },
    pushQuery(changes) {
      const query = { ...this.$route.query, ...changes }
      for (const key of Object.keys(query)) {
        if (query[key] === '' || query[key] === null || query[key] === undefined || query[key] === false) delete query[key]
      }
      const current = JSON.stringify(this.$route.query)
      if (JSON.stringify(query) !== current) this.$router.push({ query })
    },
    applySearch(value) {
      this.pushQuery({ q: value || null, page: null })
    },
    applyArchived(value) {
      this.pushQuery({ archived: value ? '1' : null, page: null })
    },
    removeFilter(key) {
      if (key === 'search') this.applySearch('')
      if (key === 'archived') this.applyArchived(false)
    },
    resetFilters() {
      this.pushQuery({ q: null, archived: null, page: null, from: null, to: null })
    },
    applyFixtureDateRange(value) {
      this.fixtureDateRange = { ...value }
      this.pushQuery({ from: value.from || null, to: value.to || null })
    },
    displayName(row) {
      return row.name || row.short_name || row.full_name || '-'
    },
    loadCounterparties(page = this.pagination.page, rowsPerPage = this.pagination.rowsPerPage) {
      this.loading = true
      this.accessDenied = false
      this.errorMessage = ''
      this.requestId = this.connection.makeRequestId('counterparties')
      this.connection.getCounterparties(this.requestId, {
        search: this.search,
        page,
        page_size: rowsPerPage,
        include_archived: this.includeArchived,
        is_active: this.includeArchived ? null : true
      })
    },
    onTableRequest({ pagination }) {
      this.pushQuery({
        page: pagination.page > 1 ? String(pagination.page) : null,
        size: pagination.rowsPerPage !== 20 ? String(pagination.rowsPerPage) : null,
        sort: pagination.sortBy && pagination.sortBy !== 'name' ? pagination.sortBy : null,
        desc: pagination.descending ? '1' : null
      })
    },
    onCounterparties(response) {
      if (response.request_id !== this.requestId) {
        return
      }
      this.loading = false
      if (response.type === 'error' || response.error) {
        this.counterparties = []
        this.accessDenied = response.error === 'access_denied'
        this.errorMessage = this.accessDenied ? '' : (response.message || 'Не удалось загрузить контрагентов')
        return
      }
      const data = response.data || {}
      const page = data.pagination || {}
      this.counterparties = data.items || []
      this.pagination = {
        page: page.page || 1,
        rowsPerPage: page.page_size || 20,
        rowsNumber: page.total || 0,
        sortBy: this.pagination.sortBy,
        descending: this.pagination.descending
      }
    },
    resetForm() {
      this.form = emptyForm()
      this.formError = ''
      this.lookupMessage = ''
      this.lookupState = 'idle'
      this.lookupSource = ''
      this.lookupDiagnostics = []
    },
    openCreateDialog() {
      this.formMode = 'create'
      this.resetForm()
      this.formDialog = true
    },
    openEditDialog(row) {
      this.formMode = 'edit'
      this.form = {
        ...emptyForm(),
        id: row.id,
        inn: row.inn || '',
        kpp: row.kpp || '',
        name: row.name || '',
        short_name: row.short_name || '',
        full_name: row.full_name || '',
        ogrn: row.ogrn || '',
        type: row.type || row.counterparty_type || 'unknown',
        status: row.status || 'unknown',
        address: row.address || '',
        legal_address: row.legal_address || '',
        actual_address: row.actual_address || '',
        phone: row.phone || '',
        email: row.email || '',
        enrich: false
      }
      this.formError = ''
      this.lookupMessage = ''
      this.lookupState = 'idle'
      this.lookupSource = row.source || ''
      this.lookupDiagnostics = []
      this.formDialog = true
    },
    applyLookupPayload(data) {
      if (!data) {
        return
      }
      this.form = {
        ...this.form,
        inn: data.inn || this.form.inn,
        kpp: data.kpp || this.form.kpp,
        name: data.name || data.short_name || this.form.name,
        short_name: data.short_name || data.name || this.form.short_name,
        full_name: data.full_name || this.form.full_name,
        ogrn: data.ogrn || this.form.ogrn,
        type: data.type || data.counterparty_type || this.form.type,
        status: data.status || this.form.status,
        address: data.address || this.form.address,
        legal_address: data.legal_address || data.address || this.form.legal_address
      }
    },
    lookupByInn() {
      const inn = normalizeDigits(this.form.inn)
      if (inn.length < 10) {
        this.lookupState = 'error'
        this.lookupMessage = 'Укажите ИНН'
        return
      }
      this.lookupLoading = true
      this.lookupMessage = ''
      this.lookupSource = ''
      this.lookupDiagnostics = []
      this.lookupRequestId = this.connection.makeRequestId('counterparty-lookup')
      this.connection.lookupCounterpartyByInn(this.lookupRequestId, inn, normalizeDigits(this.form.kpp))
    },
    onLookupResult(response) {
      if (response.request_id !== this.lookupRequestId) {
        return
      }
      this.lookupLoading = false
      if (response.type === 'error' || response.error) {
        this.lookupState = 'error'
        this.lookupMessage = response.message || 'Не удалось получить данные из DaData'
        this.lookupDiagnostics = response.diagnostics || []
        return
      }
      const diagnostics = response.diagnostics || []
      this.lookupDiagnostics = diagnostics
      if (!response.data) {
        this.lookupState = 'warning'
        this.lookupMessage = (diagnostics[0] && diagnostics[0].message) || 'DaData не вернула данные'
        return
      }
      this.applyLookupPayload(response.data)
      this.lookupSource = response.data.source || 'DaData'
      this.lookupState = diagnostics.length ? 'warning' : 'success'
      this.lookupMessage = diagnostics.length ? diagnostics[0].message : 'Реквизиты заполнены'
    },
    formPayload() {
      return {
        inn: normalizeDigits(this.form.inn),
        kpp: normalizeDigits(this.form.kpp),
        name: String(this.form.name || '').trim(),
        short_name: String(this.form.short_name || this.form.name || '').trim(),
        full_name: String(this.form.full_name || '').trim(),
        ogrn: normalizeDigits(this.form.ogrn),
        type: this.form.type,
        status: this.form.status,
        address: String(this.form.address || '').trim(),
        legal_address: String(this.form.legal_address || '').trim(),
        actual_address: String(this.form.actual_address || '').trim(),
        phone: String(this.form.phone || '').trim(),
        email: String(this.form.email || '').trim(),
        enrich: this.form.enrich
      }
    },
    saveCounterparty() {
      if (!this.canEditFinance || this.saving) return
      const payload = this.formPayload()
      if (!payload.name && !payload.inn) {
        this.formError = 'Укажите наименование или ИНН'
        return
      }
      this.saving = true
      this.formError = ''
      this.mutationRequestId = this.connection.makeRequestId('counterparty-save')
      if (this.formMode === 'edit') {
        this.connection.updateCounterparty(this.mutationRequestId, this.form.id, payload)
      } else {
        this.connection.createCounterparty(this.mutationRequestId, payload)
      }
    },
    onMutationResult(response) {
      if (response.request_id !== this.mutationRequestId) {
        return
      }
      this.saving = false
      if (response.type === 'error' || response.error) {
        this.formError = response.message || 'Не удалось сохранить контрагента'
        return
      }
      const diagnostics = response.diagnostics || []
      this.formDialog = false
      this.$q.notify({
        message: diagnostics.length ? diagnostics[0].message : 'Контрагент сохранен',
        color: diagnostics.length ? 'warning' : 'positive'
      })
      this.loadCounterparties()
    },
    confirmArchive(row) {
      if (!this.canEditFinance || this.saving) return
      this.$q.dialog({
        title: 'Архивировать контрагента',
        message: `Архивировать ${this.displayName(row)}?`,
        cancel: true,
        persistent: true
      }).onOk(() => {
        this.saving = true
        this.mutationRequestId = this.connection.makeRequestId('counterparty-archive')
        this.connection.archiveCounterparty(this.mutationRequestId, row.id)
      })
    },
    onArchiveResult(response) {
      if (response.request_id !== this.mutationRequestId) {
        return
      }
      this.saving = false
      if (response.type === 'error' || response.error) {
        this.errorMessage = response.message || 'Не удалось архивировать контрагента'
        return
      }
      this.$q.notify({ message: 'Контрагент архивирован', color: 'positive' })
      this.loadCounterparties()
    }
  }
}
</script>

<style scoped>
.counterparty-dialog {
  width: min(900px, calc(100vw - 32px));
  max-width: 100%;
  border-radius: 8px;
}

@media (max-width: 599px) {
  .counterparty-row-action {
    width: var(--sid-touch-target);
    height: var(--sid-touch-target);
  }
}
</style>
